import {
  GetFolderEntriesInput,
  GetMaterialsListAuditLogsInput,
  GetMaterialsListOrderSummaryItemsInput,
  GetPaginatedActivityFeedForProjectInput,
  GetPaginatedLabourForecastsForProjectInput,
  GetPaginatedProjectUploadsInput,
  GetScheduledEventsForProjectInput,
  GetTasksInput,
  PaginationInput,
} from '@/gql/graphql';

const projectKeys = {
  all: ['project'] as const,
  search: (searchTerm: string) =>
    [...projectKeys.all, 'search', searchTerm] as const,
  project: (projectId: string) => [...projectKeys.all, projectId] as const,
  activityFeed: (
    projectId: string,
    input?: GetPaginatedActivityFeedForProjectInput
  ) => {
    const baseKey = [
      ...projectKeys.project(projectId),
      'activityFeed',
    ] as const;
    if (!input) return baseKey;
    return [...baseKey, input] as const;
  },
  overview: (projectId: string) =>
    [...projectKeys.project(projectId), 'overview'] as const,
  location: (projectId: string) =>
    [...projectKeys.project(projectId), 'location'] as const,
  estimates: (projectId: string) =>
    [...projectKeys.project(projectId), 'estimates'] as const,
  heatLossEstimate: (projectId: string) =>
    [...projectKeys.project(projectId), 'heatLossEstimate'] as const,
  solarPotential: (projectId: string) =>
    [...projectKeys.project(projectId), 'solarPotential'] as const,
  buildingFootprints: (
    projectId: string,
    location?: { lat: number; lng: number } | null
  ) => {
    const baseKey = [
      ...projectKeys.project(projectId),
      'buildingFootprints',
    ] as const;
    if (!location) return baseKey;
    return [...baseKey, location] as const;
  },
  titleParcelAt: (
    projectId: string,
    location?: { lat: number; lng: number } | null
  ) => {
    const baseKey = [
      ...projectKeys.project(projectId),
      'titleParcelAt',
    ] as const;
    if (!location) return baseKey;
    return [...baseKey, location] as const;
  },
  profitBreakdown: (projectId: string) =>
    [...projectKeys.project(projectId), 'profitBreakdown'] as const,
  customFields: (projectId: string) =>
    [...projectKeys.project(projectId), 'customFields'] as const,
  customFieldAuditLogs: (
    projectId: string,
    fieldIdentifier: string,
    pagination?: PaginationInput
  ) => {
    const baseKey = [
      ...projectKeys.project(projectId),
      'customFields',
      fieldIdentifier,
      'auditLogs',
    ] as const;

    if (pagination) {
      return [...baseKey, pagination] as const;
    }

    return baseKey;
  },
  accountingIntegrationCategories: (projectId: string) =>
    [
      ...projectKeys.project(projectId),
      'accountingIntegrationCategories',
    ] as const,
  events: (projectId: string, input?: GetScheduledEventsForProjectInput) => {
    const baseKey = [...projectKeys.project(projectId), 'events'] as const;
    if (!input) return baseKey;
    return [...baseKey, input] as const;
  },
  tasks: (
    projectId: string,
    input?: GetTasksInput,
    pagination?: PaginationInput
  ) => {
    const baseKey = [...projectKeys.project(projectId), 'tasks'] as const;
    if (!input && !pagination) return baseKey;
    return [...baseKey, { input, pagination }] as const;
  },
  materialsList: (projectId: string) => {
    return [...projectKeys.project(projectId), 'materialsList'] as const;
  },
  materialsListMaterials: (projectId: string, pagination?: PaginationInput) => {
    const baseKey = [
      ...projectKeys.materialsList(projectId),
      'materials',
    ] as const;
    if (!pagination) return baseKey;
    return [...baseKey, pagination] as const;
  },
  materialsListSummaryItems: (
    projectId: string,
    input?: GetMaterialsListOrderSummaryItemsInput,
    pagination?: PaginationInput
  ) => {
    const baseKey = [
      ...projectKeys.materialsList(projectId),
      'summaryItems',
    ] as const;
    if (!input && !pagination) return baseKey;
    return [...baseKey, { input, pagination }] as const;
  },
  stockSources: (projectId: string) =>
    [...projectKeys.materialsList(projectId), 'stockSources'] as const,
  materialsListAuditLogs: (
    projectId: string,
    input?: GetMaterialsListAuditLogsInput,
    pagination?: PaginationInput
  ) => {
    const baseKey = [
      ...projectKeys.materialsList(projectId),
      'auditLogs',
    ] as const;
    if (!input && !pagination) return baseKey;
    return [...baseKey, { input, pagination }] as const;
  },
  purchaseOrders: (projectId: string, pagination?: PaginationInput) => {
    const baseKey = [
      ...projectKeys.project(projectId),
      'purchaseOrders',
    ] as const;
    if (!pagination) return baseKey;
    return [...baseKey, pagination] as const;
  },
  uploads: (
    projectId: string,
    input?: GetPaginatedProjectUploadsInput,
    pagination?: PaginationInput
  ) => {
    const baseKey = [...projectKeys.project(projectId), 'uploads'] as const;
    if (!input && !pagination) return baseKey;
    return [...baseKey, { input, pagination }] as const;
  },
  integrations: (projectId: string) =>
    [...projectKeys.project(projectId), 'integrations'] as const,
  canBeLinkedToSpruce: (projectId: string) =>
    [...projectKeys.project(projectId), 'canBeLinkedToSpruce'] as const,
  spruceProjectData: (projectId: string) =>
    [...projectKeys.project(projectId), 'spruceProjectData'] as const,
  invoices: (projectId: string) =>
    [...projectKeys.project(projectId), 'invoices'] as const,
  paymentSchedule: (projectId: string) =>
    [...projectKeys.project(projectId), 'paymentSchedule'] as const,
  folders: (projectId: string) => {
    return [...projectKeys.project(projectId), 'folders'] as const;
  },
  folderEntries: (projectId: string, input?: GetFolderEntriesInput) => {
    const baseKey = [
      ...projectKeys.folders(projectId),
      'folderEntries',
    ] as const;
    if (!input) return baseKey;
    return [...baseKey, { input }] as const;
  },
  folderEntry: (projectId: string, folderEntryId: string) => {
    return [...projectKeys.folderEntries(projectId), folderEntryId] as const;
  },
  labourForecasts: (
    projectId: string,
    pagination?: PaginationInput,
    input?: GetPaginatedLabourForecastsForProjectInput
  ) => {
    const baseKey = [
      ...projectKeys.project(projectId),
      'labourForecasts',
    ] as const;
    if (!pagination && !input) return baseKey;
    return [...baseKey, { pagination, input }] as const;
  },
  potentialPipelineTransitions: (projectId: string) => {
    return [
      ...projectKeys.project(projectId),
      'potentialPipelineTransitions',
    ] as const;
  },
  commitments: (projectId: string, pagination?: PaginationInput) => {
    const baseKey = [...projectKeys.project(projectId), 'commitments'] as const;
    if (!pagination) return baseKey;
    return [...baseKey, pagination] as const;
  },
};

export default projectKeys;
